import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { DashboardLayout } from '../components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Alert, AlertDescription } from '../components/ui/alert';
import {
  AlertTriangle, AlertCircle, Check, X, Loader2, Search, MessageSquare,
  ChevronDown, ChevronUp, FileCode, SquareCheck, Square, Sparkles, RefreshCw, Zap,
  Building2, Calendar, FileText,
} from 'lucide-react';
import { apiFetch } from '../lib/supabase';
import { HITLEvidenceDrawer } from '../components/HITLEvidenceDrawer';
import { ModelBadge } from '../components/ValidationKpiDashboard';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function HITLReviewPage() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('');
  const [expandedId, setExpandedId] = useState(null);
  const [reviewNotes, setReviewNotes] = useState('');
  const [processing, setProcessing] = useState(null);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [bulkProcessing, setBulkProcessing] = useState(false);
  const [successMsg, setSuccessMsg] = useState('');
  const [aiReviewing, setAiReviewing] = useState(false);
  const [showAIBulkConfirm, setShowAIBulkConfirm] = useState(false);
  const [projectsList, setProjectsList] = useState([]);
  const [auditsList, setAuditsList] = useState([]);
  const [selectedProject, setSelectedProject] = useState(''); // '' = all
  const [selectedAudit, setSelectedAudit] = useState(''); // '' = all
  const [totalCount, setTotalCount] = useState(0);
  // Sprint 3 — drawer state for the side-by-side evidence panel.
  const [evidenceItemId, setEvidenceItemId] = useState(null);

  const [error, setError] = useState('');

  const fetchQueue = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const params = new URLSearchParams();
      if (selectedProject) params.set('project_id', selectedProject);
      if (selectedAudit) params.set('audit_id', selectedAudit);
      params.set('limit', '1000');
      const resp = await apiFetch(`${API_URL}/api/audits/hitl/queue?${params}`);
      if (resp.ok) {
        const data = await resp.json();
        setItems(data.items || []);
        setProjectsList(data.projects || []);
        setAuditsList(data.audits || []);
        setTotalCount(data.total || 0);
      } else {
        // Surface the failure instead of silently rendering "All clear".
        // Until Feb 25, 2026 this branch was a silent no-op, which
        // hid the bug where the queue 400'd on every cold open
        // (it used to require a filter that the page didn't set).
        const txt = await resp.text().catch(() => '');
        let detail = txt;
        try { detail = JSON.parse(txt).detail || txt; } catch (_) { /* keep raw */ }
        setError(`Could not load HITL queue (${resp.status}): ${String(detail).slice(0, 200)}`);
        setItems([]);
        setProjectsList([]);
        setAuditsList([]);
        setTotalCount(0);
      }
    } catch (err) {
      console.error('fetchQueue failed:', err);
      setError(err.message || 'Network error loading HITL queue');
    } finally {
      setLoading(false);
    }
  }, [selectedProject, selectedAudit]);

  useEffect(() => { fetchQueue(); }, [fetchQueue]);

  // When project changes, clear stale audit selection if the audit no longer belongs to the selected project
  useEffect(() => {
    if (selectedAudit && auditsList.length > 0) {
      const stillValid = auditsList.find(a => a.id === selectedAudit);
      if (!stillValid) setSelectedAudit('');
    }
  }, [auditsList, selectedAudit]);

  const triggerAIReview = async () => {
    setAiReviewing(true);
    try {
      // Find items without an _ai_review and send them in batch
      const pending = items
        .filter(i => !i.properties?._ai_review)
        .slice(0, 50)
        .map(i => i.id);
      const resp = await apiFetch(`${API_URL}/api/audits/hitl/ai-review`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ item_ids: pending, max_items: 50 }),
      });
      if (resp.ok) {
        setSuccessMsg(`AI pre-review started for ${pending.length} items. Refresh in a minute to see recommendations.`);
        setTimeout(() => setSuccessMsg(''), 5000);
        // Auto-refresh after 30s to pull in early AI results
        setTimeout(() => fetchQueue(), 30000);
      }
    } catch (err) { console.error(err); }
    finally { setAiReviewing(false); }
  };

  const reviewItem = async (itemId, action, notes = '') => {
    setProcessing(itemId);
    try {
      const resp = await apiFetch(`${API_URL}/api/audits/hitl/${itemId}/review`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action, notes, reviewed_by: 'admin' }),
      });
      if (resp.ok) {
        setItems(prev => prev.filter(i => i.id !== itemId));
        setExpandedId(null);
        setReviewNotes('');
        setSuccessMsg(`Item ${action === 'approve' ? 'approved' : action === 'reject' ? 'rejected' : 'reviewed'} successfully`);
        setTimeout(() => setSuccessMsg(''), 3000);
      }
    } catch (err) { console.error(err); }
    finally { setProcessing(null); }
  };

  const bulkApprove = async () => {
    if (selectedIds.size === 0) return;
    setBulkProcessing(true);
    try {
      const payload = Array.from(selectedIds).map(id => ({ id, action: 'approve' }));
      const resp = await apiFetch(`${API_URL}/api/audits/hitl/bulk-review`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (resp.ok) {
        setItems(prev => prev.filter(i => !selectedIds.has(i.id)));
        setSelectedIds(new Set());
        setSuccessMsg(`${payload.length} items approved`);
        setTimeout(() => setSuccessMsg(''), 3000);
      }
    } catch (err) { console.error(err); }
    finally { setBulkProcessing(false); }
  };

  const selectAIApproved = () => {
    const aiApproved = filtered
      .filter(i => i.properties?._ai_review?.recommendation === 'approve')
      .map(i => i.id);
    setSelectedIds(new Set(aiApproved));
  };

  const bulkApproveAIRecommended = async () => {
    const aiApprovedItems = items.filter(i => i.properties?._ai_review?.recommendation === 'approve');
    if (aiApprovedItems.length === 0) {
      setSuccessMsg('No AI-approved items found. Click "AI Pre-review" first.');
      setTimeout(() => setSuccessMsg(''), 3500);
      setShowAIBulkConfirm(false);
      return;
    }
    setBulkProcessing(true);
    try {
      const payload = aiApprovedItems.map(i => ({ id: i.id, action: 'approve' }));
      // Send in chunks of 100 to be safe with payload size
      let approved = 0;
      for (let i = 0; i < payload.length; i += 100) {
        const chunk = payload.slice(i, i + 100);
        const resp = await apiFetch(`${API_URL}/api/audits/hitl/bulk-review`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(chunk),
        });
        if (resp.ok) approved += chunk.length;
      }
      const approvedSet = new Set(aiApprovedItems.map(i => i.id));
      setItems(prev => prev.filter(i => !approvedSet.has(i.id)));
      setSelectedIds(new Set());
      setShowAIBulkConfirm(false);
      setSuccessMsg(`${approved} AI-recommended items approved in 1 click`);
      setTimeout(() => setSuccessMsg(''), 4000);
    } catch (err) {
      console.error('bulkApproveAIRecommended failed:', err);
      setSuccessMsg('Bulk approve failed — check console');
      setTimeout(() => setSuccessMsg(''), 4000);
    } finally {
      setBulkProcessing(false);
    }
  };

  const toggleSelect = (id) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const selectAll = () => setSelectedIds(new Set(filtered.map(i => i.id)));
  const deselectAll = () => setSelectedIds(new Set());

  const filtered = items.filter(item => {
    if (!filter) return true;
    const q = filter.toLowerCase();
    return (item.asset_name || '').toLowerCase().includes(q) ||
           (item.ifc_class || '').toLowerCase().includes(q) ||
           (item.element_id || '').toLowerCase().includes(q) ||
           (item.ifc_file_name || '').toLowerCase().includes(q);
  });

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="h-8 w-8 animate-spin text-accent" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="max-w-5xl mx-auto">
        <div className="mb-6">
          <h1 className="text-3xl font-bold text-foreground mb-2 flex items-center gap-2" data-testid="hitl-title">
            HITL Review Queue
            <ModelBadge />
          </h1>
          <p className="text-muted-foreground">
            Review flagged items that need human verification before certification
          </p>
        </div>

        {successMsg && (
          <Alert className="mb-4 border-[hsl(var(--success))]">
            <Check className="h-4 w-4 text-[hsl(var(--success))]" />
            <AlertDescription>{successMsg}</AlertDescription>
          </Alert>
        )}

        {error && (
          <Alert variant="destructive" className="mb-4" data-testid="hitl-queue-error">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>
              {error}
              <button
                className="ml-3 underline text-xs"
                onClick={() => fetchQueue()}
                data-testid="hitl-queue-retry-btn"
              >Retry</button>
            </AlertDescription>
          </Alert>
        )}

        {items.length === 0 ? (
          <Card data-testid="hitl-queue-empty">
            <CardContent className="py-16 text-center">
              <Check className="h-12 w-12 mx-auto mb-4 text-[hsl(var(--success))]" />
              <h3 className="text-lg font-semibold mb-2">{error ? 'Could not load queue' : 'All clear'}</h3>
              <p className="text-sm text-muted-foreground">
                {error ? 'See error above and click Retry.' : 'No items pending review'}
              </p>
            </CardContent>
          </Card>
        ) : (
          <>
            {/* Project + IFC file filters */}
            <Card className="mb-4 border-accent/30">
              <CardContent className="pt-4 pb-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs font-medium text-muted-foreground mb-1.5 flex items-center gap-1">
                      <Building2 className="h-3.5 w-3.5" /> Project
                    </label>
                    <select
                      value={selectedProject}
                      onChange={(e) => { setSelectedProject(e.target.value); setSelectedAudit(''); }}
                      className="w-full h-9 px-3 rounded-md border border-input bg-background text-sm focus:outline-none focus:ring-2 focus:ring-accent"
                      data-testid="hitl-project-filter"
                    >
                      <option value="">All projects ({totalCount} flagged)</option>
                      {projectsList.map(p => (
                        <option key={p.id} value={p.id}>{p.name} ({p.code})</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="text-xs font-medium text-muted-foreground mb-1.5 flex items-center gap-1">
                      <FileCode className="h-3.5 w-3.5" /> IFC File / Audit
                    </label>
                    <select
                      value={selectedAudit}
                      onChange={(e) => setSelectedAudit(e.target.value)}
                      className="w-full h-9 px-3 rounded-md border border-input bg-background text-sm focus:outline-none focus:ring-2 focus:ring-accent"
                      data-testid="hitl-audit-filter"
                      disabled={auditsList.length === 0}
                    >
                      <option value="">All IFC files</option>
                      {auditsList.map(a => (
                        <option key={a.id} value={a.id}>
                          {a.ifc_file_name} — {a.created_at ? new Date(a.created_at).toLocaleDateString() : ''}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
                {(selectedProject || selectedAudit) && (
                  <div className="mt-3 flex items-center justify-between flex-wrap gap-2">
                    <p className="text-xs text-muted-foreground">
                      Showing <strong className="text-foreground">{items.length}</strong> of <strong className="text-foreground">{totalCount}</strong> flagged items matching filter
                    </p>
                    <Button variant="ghost" size="sm" onClick={() => { setSelectedProject(''); setSelectedAudit(''); }}
                      data-testid="hitl-clear-filters">
                      <X className="h-3 w-3 mr-1" /> Clear filters
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* AI Auto-Approve hero — only shows when AI has recommendations to approve */}
            {(() => {
              const aiApprovedCount = items.filter(i => i.properties?._ai_review?.recommendation === 'approve').length;
              if (aiApprovedCount === 0) return null;
              return (
                <div className="mb-4 p-4 rounded-lg border border-accent/40 bg-gradient-to-r from-accent/10 via-accent/5 to-transparent flex items-center gap-4 flex-wrap"
                  data-testid="ai-bulk-banner">
                  <div className="p-2 rounded-md bg-accent/20">
                    <Zap className="h-5 w-5 text-accent" />
                  </div>
                  <div className="flex-1 min-w-[200px]">
                    <p className="text-sm font-semibold text-foreground">
                      AI has pre-approved {aiApprovedCount} item{aiApprovedCount !== 1 ? 's' : ''}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      One click to clear them all from the queue. AI's reasoning is preserved on each item.
                    </p>
                  </div>
                  <Button
                    onClick={() => setShowAIBulkConfirm(true)}
                    disabled={bulkProcessing}
                    className="bg-accent hover:bg-accent/90 text-accent-foreground font-semibold"
                    data-testid="ai-bulk-approve-hero"
                  >
                    {bulkProcessing ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Zap className="h-4 w-4 mr-2" />}
                    Auto-Approve {aiApprovedCount}
                  </Button>
                </div>
              );
            })()}

            {/* AI Bulk Approve confirmation modal */}
            {showAIBulkConfirm && (
              <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
                onClick={() => !bulkProcessing && setShowAIBulkConfirm(false)}
                data-testid="ai-bulk-confirm-overlay">
                <Card className="max-w-md w-full" onClick={(e) => e.stopPropagation()}>
                  <CardContent className="pt-6 space-y-4">
                    <div className="flex items-start gap-3">
                      <div className="p-2 rounded-md bg-accent/20 shrink-0">
                        <Sparkles className="h-5 w-5 text-accent" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-foreground mb-1">Confirm AI Auto-Approve</h3>
                        <p className="text-sm text-muted-foreground">
                          You're about to approve <strong>
                            {items.filter(i => i.properties?._ai_review?.recommendation === 'approve').length}
                          </strong> items
                          that GPT-4o recommended approving. Each item's AI reasoning is recorded on the
                          aim_register so the audit trail is preserved.
                        </p>
                        <p className="text-xs text-muted-foreground mt-2">
                          This action can be reverted per-item if needed (re-flag from results page).
                        </p>
                      </div>
                    </div>
                    <div className="flex gap-2 justify-end">
                      <Button variant="outline" onClick={() => setShowAIBulkConfirm(false)}
                        disabled={bulkProcessing} data-testid="ai-bulk-cancel">
                        Cancel
                      </Button>
                      <Button onClick={bulkApproveAIRecommended}
                        disabled={bulkProcessing}
                        className="bg-accent hover:bg-accent/90 text-accent-foreground"
                        data-testid="ai-bulk-confirm">
                        {bulkProcessing ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Zap className="h-4 w-4 mr-2" />}
                        Approve All
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              </div>
            )}

            {/* Controls bar */}
            <div className="card-elevated p-4 mb-4">
              <div className="flex flex-col sm:flex-row gap-3 justify-between items-center">
                <div className="relative flex-1 max-w-sm">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input placeholder="Filter by name, class, file..." value={filter}
                    onChange={(e) => setFilter(e.target.value)} className="pl-9" data-testid="hitl-search" />
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-xs text-muted-foreground">
                    {filtered.length} item{filtered.length !== 1 ? 's' : ''} flagged
                    {selectedIds.size > 0 && ` · ${selectedIds.size} selected`}
                  </span>
                  <Button variant="outline" size="sm" onClick={fetchQueue} data-testid="hitl-refresh">
                    <RefreshCw className="h-3.5 w-3.5 mr-1" /> Refresh
                  </Button>
                  <Button variant="outline" size="sm" onClick={triggerAIReview}
                    disabled={aiReviewing} data-testid="hitl-ai-review-btn"
                    className="border-accent/50 text-accent hover:bg-accent/10">
                    {aiReviewing ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> :
                      <Sparkles className="h-3.5 w-3.5 mr-1" />}
                    AI Pre-review
                  </Button>
                  <Button variant="outline" size="sm" onClick={selectAIApproved}
                    data-testid="hitl-select-ai-approved">
                    <SquareCheck className="h-3.5 w-3.5 mr-1" /> Select AI-Approved
                  </Button>
                  <Button variant="outline" size="sm" onClick={selectedIds.size === filtered.length ? deselectAll : selectAll}>
                    {selectedIds.size === filtered.length ? <Square className="h-3.5 w-3.5 mr-1" /> : <SquareCheck className="h-3.5 w-3.5 mr-1" />}
                    {selectedIds.size === filtered.length ? 'Deselect' : 'Select All'}
                  </Button>
                  {selectedIds.size > 0 && (
                    <Button size="sm" onClick={bulkApprove} disabled={bulkProcessing}
                      className="bg-[hsl(var(--success))] hover:bg-[hsl(var(--success))]/90" data-testid="bulk-approve">
                      {bulkProcessing ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <Check className="h-3.5 w-3.5 mr-1" />}
                      Approve {selectedIds.size}
                    </Button>
                  )}
                </div>
              </div>
            </div>

            {/* Items list */}
            <div className="space-y-2">
              {filtered.map((item) => {
                const isExpanded = expandedId === item.id;
                const isSelected = selectedIds.has(item.id);
                const issues = item.issues || [];
                const omConflicts = item.properties?._om_conflicts || {};
                const cobieAnalysis = item.properties?._cobie_analysis;
                const aiReview = item.properties?._ai_review;
                const aiBadgeStyle = aiReview?.recommendation === 'approve'
                  ? 'bg-[hsl(var(--success))]/10 text-[hsl(var(--success))] border-[hsl(var(--success))]/30'
                  : aiReview?.recommendation === 'reject'
                  ? 'bg-[hsl(var(--destructive))]/10 text-[hsl(var(--destructive))] border-[hsl(var(--destructive))]/30'
                  : 'bg-muted text-muted-foreground border-border';

                return (
                  <Card key={item.id} className={`transition-colors ${isSelected ? 'border-accent' : ''}`}
                    data-testid={`hitl-item-${item.id}`}>
                    <CardContent className="py-3">
                      {/* Main row */}
                      <div className="flex items-center gap-3">
                        <button onClick={() => toggleSelect(item.id)} className="p-0.5">
                          {isSelected ?
                            <SquareCheck className="h-4 w-4 text-accent" /> :
                            <Square className="h-4 w-4 text-muted-foreground" />}
                        </button>

                        <AlertTriangle className="h-4 w-4 text-[hsl(var(--warning))] shrink-0" />

                        <div className="flex-1 min-w-0 cursor-pointer" onClick={() => setExpandedId(isExpanded ? null : item.id)}>
                          <div className="flex items-center gap-2 mb-0.5 flex-wrap">
                            <span className="font-medium text-sm text-foreground truncate">{item.asset_name || item.element_id}</span>
                            <Badge variant="outline" className="text-[10px]">{item.ifc_class?.replace('Ifc', '')}</Badge>
                            <Badge className="bg-[hsl(var(--warning))]/10 text-[hsl(var(--warning))] border border-[hsl(var(--warning))]/30 text-[10px]">
                              {item.confidence_score}%
                            </Badge>
                            {aiReview && (
                              <Badge className={`${aiBadgeStyle} border text-[10px] flex items-center gap-1`}
                                data-testid={`ai-rec-${item.id}`}>
                                <Sparkles className="h-2.5 w-2.5" />
                                AI: {aiReview.recommendation === 'approve' ? 'Approve' :
                                     aiReview.recommendation === 'reject' ? 'Reject' : 'Review'}
                              </Badge>
                            )}
                          </div>
                          <div className="flex items-center gap-3 text-xs text-muted-foreground flex-wrap">
                            <span className="flex items-center gap-1">
                              <FileCode className="h-3 w-3" /> {item.ifc_file_name}
                            </span>
                            {item.project_name && (
                              <span className="flex items-center gap-1" data-testid={`project-${item.id}`}>
                                <Building2 className="h-3 w-3" /> {item.project_name}
                              </span>
                            )}
                            {item.audit_date && (
                              <span className="flex items-center gap-1" data-testid={`date-${item.id}`}>
                                <Calendar className="h-3 w-3" /> {new Date(item.audit_date).toLocaleDateString()}
                              </span>
                            )}
                            {item.storey && <span>Storey: {item.storey}</span>}
                            {Object.keys(omConflicts).length > 0 && (
                              <Badge className="bg-[hsl(var(--destructive))]/10 text-[hsl(var(--destructive))] border border-[hsl(var(--destructive))]/30 text-[10px]">
                                O&M Conflict
                              </Badge>
                            )}
                          </div>
                        </div>

                        <div className="flex items-center gap-1.5 shrink-0">
                          <Button size="sm" variant="outline"
                            className="h-7 text-xs border-[hsl(var(--accent))]/60 text-[hsl(var(--accent-foreground))] bg-[hsl(var(--accent))]/15 hover:bg-[hsl(var(--accent))]/25"
                            onClick={() => setEvidenceItemId(item.id)}
                            data-testid={`evidence-${item.id}`}
                            title="Open side-by-side IFC vs BEP evidence + decision log">
                            <FileText className="h-3 w-3 mr-0.5" />
                            Evidence
                          </Button>
                          <Button size="sm" variant="outline"
                            className="h-7 text-xs border-[hsl(var(--success))]/50 text-[hsl(var(--success))] hover:bg-[hsl(var(--success))]/10"
                            onClick={() => reviewItem(item.id, 'approve')}
                            disabled={processing === item.id} data-testid={`approve-${item.id}`}>
                            {processing === item.id ? <Loader2 className="h-3 w-3 animate-spin" /> : <Check className="h-3 w-3 mr-0.5" />}
                            Approve
                          </Button>
                          <Button size="sm" variant="outline"
                            className="h-7 text-xs border-[hsl(var(--destructive))]/50 text-[hsl(var(--destructive))] hover:bg-[hsl(var(--destructive))]/10"
                            onClick={() => reviewItem(item.id, 'reject')}
                            disabled={processing === item.id} data-testid={`reject-${item.id}`}>
                            <X className="h-3 w-3 mr-0.5" /> Reject
                          </Button>
                          <button onClick={() => setExpandedId(isExpanded ? null : item.id)} className="p-1">
                            {isExpanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                          </button>
                        </div>
                      </div>

                      {/* Expanded details */}
                      {isExpanded && (
                        <div className="mt-3 pt-3 border-t border-border space-y-3">
                          {/* AI Pre-review */}
                          {aiReview && (
                            <div className={`p-3 rounded border ${
                              aiReview.recommendation === 'approve' ? 'border-[hsl(var(--success))]/30 bg-[hsl(var(--success))]/5' :
                              aiReview.recommendation === 'reject' ? 'border-[hsl(var(--destructive))]/30 bg-[hsl(var(--destructive))]/5' :
                              'border-border bg-muted/30'
                            }`} data-testid={`ai-review-${item.id}`}>
                              <div className="flex items-center gap-2 mb-1">
                                <Sparkles className="h-3.5 w-3.5 text-accent" />
                                <span className="text-xs font-semibold">
                                  AI Recommendation: {aiReview.recommendation === 'approve' ? 'Approve' :
                                                      aiReview.recommendation === 'reject' ? 'Reject' : 'Human Decide'}
                                </span>
                                {typeof aiReview.confidence === 'number' && (
                                  <Badge variant="outline" className="text-[10px]">
                                    {Math.round(aiReview.confidence * 100)}% confidence
                                  </Badge>
                                )}
                              </div>
                              {aiReview.reasoning && (
                                <p className="text-xs text-foreground/80 mb-1">{aiReview.reasoning}</p>
                              )}
                              {Array.isArray(aiReview.key_factors) && aiReview.key_factors.length > 0 && (
                                <ul className="text-[11px] text-muted-foreground list-disc pl-4 space-y-0.5">
                                  {aiReview.key_factors.map((f, idx) => (
                                    <li key={`${item.id}-kf-${idx}-${String(f).slice(0, 20)}`}>{f}</li>
                                  ))}
                                </ul>
                              )}
                            </div>
                          )}

                          {/* Issues */}
                          {issues.length > 0 && (
                            <div>
                              <p className="text-xs font-semibold text-muted-foreground mb-1.5">Issues Found:</p>
                              <div className="space-y-1">
                                {issues.map((issue, idx) => (
                                  <div key={`${item.id}-issue-${idx}-${String(issue).slice(0, 30)}`}
                                    className="text-xs text-foreground bg-muted/50 p-2 rounded">
                                    {issue}
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}

                          {/* O&M Conflicts */}
                          {Object.keys(omConflicts).length > 0 && (
                            <div>
                              <p className="text-xs font-semibold text-[hsl(var(--destructive))] mb-1.5">O&M Conflicts:</p>
                              {Object.entries(omConflicts).map(([key, val]) => (
                                <div key={key} className="text-xs bg-[hsl(var(--destructive))]/5 p-2 rounded mb-1">
                                  <strong>{key}:</strong> IFC says <code>{val.ifc}</code>, O&M says <code>{val.pdf}</code>
                                </div>
                              ))}
                            </div>
                          )}

                          {/* COBie Analysis */}
                          {cobieAnalysis && (
                            <div>
                              <p className="text-xs font-semibold text-accent mb-1.5">COBie Analysis:</p>
                              <div className="text-xs bg-accent/5 p-2 rounded">
                                Handover ready: <strong>{cobieAnalysis.handover_ready ? 'Yes' : 'No'}</strong> |
                                Status: <strong>{cobieAnalysis.cobie_status}</strong>
                                {cobieAnalysis.missing_fm?.length > 0 && (
                                  <span> | Missing: {cobieAnalysis.missing_fm.join(', ')}</span>
                                )}
                              </div>
                            </div>
                          )}

                          {/* Approve with notes */}
                          <div className="flex items-center gap-2">
                            <Input
                              placeholder="Add review notes (optional)..."
                              value={expandedId === item.id ? reviewNotes : ''}
                              onChange={(e) => setReviewNotes(e.target.value)}
                              className="text-xs h-8 flex-1"
                              data-testid={`notes-${item.id}`}
                            />
                            <Button size="sm" className="h-8 text-xs"
                              onClick={() => reviewItem(item.id, 'approve_with_notes', reviewNotes)}
                              disabled={processing === item.id}>
                              <MessageSquare className="h-3 w-3 mr-1" /> Approve with Notes
                            </Button>
                          </div>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </>
        )}
      </div>

      {/* Sprint 3 — Side-by-side IFC vs PDF evidence drawer.
          Mounted at the page root so the right-side overlay covers the
          full viewport regardless of scroll position. */}
      {evidenceItemId && (
        <HITLEvidenceDrawer
          itemId={evidenceItemId}
          onClose={() => setEvidenceItemId(null)}
          onReviewApplied={(id, action) => {
            // Optimistically remove the item from the queue + close the drawer.
            setItems((prev) => prev.filter((i) => i.id !== id));
            setEvidenceItemId(null);
            setSuccessMsg(`Item ${action === 'reject' ? 'rejected' : 'approved'} via evidence drawer`);
            setTimeout(() => setSuccessMsg(''), 3000);
          }}
        />
      )}
    </DashboardLayout>
  );
}
